import { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, UserPlus, Users } from 'lucide-react';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { cn } from '@/app/components/ui/utils';
import { useFindCrew } from '@/hooks/use-crew';
import type { CrewFinderCandidate } from '@/lib/types';
import type { BoardProject } from '@/hooks/use-staffing-board';
import { formatDateOnly } from '@/lib/date-utils';

type FindCrewProjectTarget = Pick<BoardProject, 'id' | 'job_id' | 'project_id' | 'name' | 'start_date' | 'end_date' | 'location' | 'client_name'>;

interface FindCrewDialogProps {
  open: boolean;
  project: FindCrewProjectTarget | null;
  onClose: () => void;
  onAssign: (candidate: CrewFinderCandidate) => void;
}

function formatDisplayDate(value: string | null | undefined) {
  return formatDateOnly(value);
}

function effectVariant(effect: CrewFinderCandidate['reasons'][number]['effect']) {
  switch (effect) {
    case 'positive':
      return 'secondary';
    case 'negative':
      return 'destructive';
    default:
      return 'outline';
  }
}

function effectClass(effect: CrewFinderCandidate['reasons'][number]['effect']) {
  switch (effect) {
    case 'positive':
      return 'border-green-200 bg-green-50 text-green-800 hover:bg-green-50';
    case 'negative':
      return '';
    default:
      return 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-50';
  }
}

function CandidateReasons({ candidate }: { candidate: CrewFinderCandidate }) {
  if (candidate.eligible && candidate.reasons.length === 0) {
    return null;
  }
  const items = candidate.eligible ? candidate.reasons : candidate.exclusions.map((ex) => ({
    code: ex.code,
    label: ex.label,
    effect: 'neutral' as const,
  }));
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item) => (
        <Badge
          key={`${item.code}-${item.label}`}
          variant={effectVariant(item.effect)}
          className={cn('text-[11px] font-medium', effectClass(item.effect))}
        >
          {item.label}
        </Badge>
      ))}
    </div>
  );
}

function CandidateCard({
  candidate,
  onAssign,
}: {
  candidate: CrewFinderCandidate;
  onAssign?: (candidate: CrewFinderCandidate) => void;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border p-3 shadow-sm transition-colors',
        candidate.eligible ? 'border-green-200 bg-green-50/40' : 'border-gray-200 bg-gray-50 opacity-80',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-gray-900">{candidate.name}</p>
            <Badge variant="outline" className="border-gray-200 bg-white text-[11px] text-gray-600">
              {candidate.status}
            </Badge>
          </div>
          <p className="mt-0.5 text-xs text-gray-500">
            Tech #{candidate.tech_id} · Person #{candidate.person_id}
          </p>
        </div>
        {candidate.eligible && onAssign && (
          <Button type="button" size="sm" onClick={() => onAssign(candidate)} className="shrink-0">
            <UserPlus className="h-4 w-4" />
            Assign
          </Button>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {candidate.eligible ? (
          <Badge variant="secondary" className="bg-blue-50 text-blue-700 hover:bg-blue-50">
            Score {candidate.score ?? 0}
          </Badge>
        ) : (
          <Badge variant="outline" className="border-gray-200 bg-white text-gray-500">
            Excluded
          </Badge>
        )}
        {candidate.eligible && (
          <Badge variant="outline" className="border-green-200 bg-white text-green-700">
            Eligible
          </Badge>
        )}
      </div>

      <div className="mt-3">
        <CandidateReasons candidate={candidate} />
      </div>
    </div>
  );
}

export function FindCrewDialog({
  open,
  project,
  onClose,
  onAssign,
}: FindCrewDialogProps) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [requiredCertLevel, setRequiredCertLevel] = useState('');
  const [requiredSkills, setRequiredSkills] = useState('');

  useEffect(() => {
    if (!open || !project) return;
    setStartDate(project.start_date ?? '');
    setEndDate(project.end_date ?? '');
    setRequiredCertLevel('');
    setRequiredSkills('');
  }, [open, project]);

  const requiredSkillsList = useMemo(
    () => requiredSkills.split(',').map((skill) => skill.trim()).filter(Boolean),
    [requiredSkills],
  );

  const query = useFindCrew(
    {
      project: project?.project_id,
      job: project?.project_id == null ? project?.job_id : undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      required_cert_level: requiredCertLevel ? Number(requiredCertLevel) : undefined,
      required_skills: requiredSkillsList,
    },
    { enabled: open },
  );

  const eligibleCandidates = query.data?.candidates.filter((candidate) => candidate.eligible) ?? [];
  const excludedCandidates = query.data?.candidates.filter((candidate) => !candidate.eligible) ?? [];
  const hasCandidates = (query.data?.candidates.length ?? 0) > 0;
  const showEmptyState = !query.isLoading && !query.isError && !hasCandidates;

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent className="flex max-h-[90vh] w-[calc(100%-1rem)] max-w-5xl flex-col overflow-hidden sm:w-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-600" />
            Find crew
          </DialogTitle>
          <DialogDescription>
            Review ranked candidates for {project?.name ?? 'this project'} and assign them directly from the list.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 border-b pb-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="find-crew-start-date">Start date</Label>
            <Input
              id="find-crew-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="find-crew-end-date">End date</Label>
            <Input
              id="find-crew-end-date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="find-crew-cert-level">Required cert level</Label>
            <Input
              id="find-crew-cert-level"
              type="number"
              min={1}
              step={1}
              placeholder="Optional"
              value={requiredCertLevel}
              onChange={(e) => setRequiredCertLevel(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="find-crew-skills">Required skills</Label>
            <Input
              id="find-crew-skills"
              type="text"
              placeholder="Rigging, rescue, welding"
              value={requiredSkills}
              onChange={(e) => setRequiredSkills(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto pr-1">
          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
            <div className="grid gap-1 md:grid-cols-2">
              <div>
                <span className="font-medium text-gray-800">Project:</span> {project?.name ?? '—'}
              </div>
              <div>
                <span className="font-medium text-gray-800">Location:</span> {project?.location ?? '—'}
              </div>
              <div>
                <span className="font-medium text-gray-800">Client:</span> {project?.client_name ?? '—'}
              </div>
              <div>
                <span className="font-medium text-gray-800">Range:</span> {formatDisplayDate(startDate)} → {formatDisplayDate(endDate)}
              </div>
            </div>
          </div>

          {query.isLoading && (
            <div className="flex items-center justify-center rounded-lg border border-dashed border-gray-200 px-4 py-12 text-sm text-gray-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Finding candidates…
            </div>
          )}

          {query.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              Unable to load crew recommendations.
            </div>
          )}

          {showEmptyState && (
            <div className="rounded-lg border border-dashed border-gray-200 px-4 py-10 text-center">
              <Search className="mx-auto h-8 w-8 text-gray-300" />
              <p className="mt-3 text-sm font-medium text-gray-700">
                No eligible candidates found.
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Try relaxing the cert or skill filters, or widening the date range.
              </p>
            </div>
          )}

          {hasCandidates && (
            <div className="space-y-4">
              {query.data?.eligible_count === 0 && excludedCandidates.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  No eligible candidates matched the current criteria. Excluded techs are listed below.
                </div>
              )}

              {eligibleCandidates.length > 0 && (
                <div className="space-y-3">
                  {eligibleCandidates.map((candidate) => (
                    <CandidateCard key={candidate.tech_id} candidate={candidate} onAssign={onAssign} />
                  ))}
                </div>
              )}

              {excludedCandidates.length > 0 && eligibleCandidates.length > 0 && (
                <div className="relative py-2">
                  <div className="border-t border-dashed border-gray-300" />
                  <div className="absolute left-1/2 top-[-0.65rem] -translate-x-1/2 bg-white px-3 text-[11px] font-medium uppercase tracking-wide text-gray-400">
                    Hard-filter line
                  </div>
                </div>
              )}

              {excludedCandidates.length > 0 && (
                <div className="space-y-3">
                  {excludedCandidates.map((candidate) => (
                    <CandidateCard key={candidate.tech_id} candidate={candidate} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
